/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    // Hooks
    useStyleConfig,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {HOME_BREADCRUMB, HOME_HREF} from '../../constants'
import SEOStructuredData from '../seo-structured-data'

// Others
import {categoryUrlBuilder} from '../../utils/url'
import useMultiSite from '../../hooks/use-multi-site'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories, display a breadcrumb and it's items.
 */
const Breadcrumb = ({categories, product, type, pageData, children, ...rest}) => {
    const intl = useIntl()
    const styles = useStyleConfig('Breadcrumb')
    const {buildUrl} = useMultiSite()
    const isPDP = Boolean(product)
    const breadcrumbRef = useRef(null)
    const shouldScrollToEnd = useBreakpointValue({base: true, sm: true, md: true, lg: false})

    useEffect(() => {
        if (breadcrumbRef.current && shouldScrollToEnd) {
            const breadcrumbList = breadcrumbRef.current.querySelector('ol')

            if (breadcrumbList) {
                breadcrumbList.scrollTo({
                    left: breadcrumbList.scrollWidth,
                    behavior: 'smooth'
                })
            }
        }
    }, [shouldScrollToEnd])

    return (
        <>
            <SEOStructuredData
                data={{
                    categories,
                    product,
                    ...pageData
                }}
                type={type || 'breadcrumb'}
            ></SEOStructuredData>
            <ChakraBreadcrumb
                ref={breadcrumbRef}
                className="sf-breadcrumb"
                textAlign={'left'}
                w="full"
                p={['3px 10px', null, '3px 20px']}
                borderTopWidth={1}
                borderBottomWidth={1}
                borderColor="borderGray"
                {...styles.container}
                separator={'/'}
                spacing={'3'}
                {...rest}
            >
                {/* The first breadcrumb is always homepage */}
                <ChakraBreadcrumbItem key="home">
                    <ChakraBreadcrumbLink as={RouteLink} to={buildUrl(HOME_HREF)}>
                        {intl.formatMessage(HOME_BREADCRUMB)}
                    </ChakraBreadcrumbLink>
                </ChakraBreadcrumbItem>

                {/* CLP / PDP breadcrumbs */}
                {categories?.map((category, index) => {
                    const isLastCategory = !product && index === categories.length - 1

                    return (
                        <ChakraBreadcrumbItem
                            key={category.id}
                            data-testid="sf-crumb-item"
                            isCurrentPage={isLastCategory}
                        >
                            {isLastCategory ? (
                                <ChakraBreadcrumbLink {...styles.lastitem}>
                                    {isPDP
                                        ? category?.displayName
                                            ? category?.displayName
                                            : category.name
                                        : category?.c_seoDisplayName || category?.name}
                                </ChakraBreadcrumbLink>
                            ) : (
                                <ChakraBreadcrumbLink
                                    as={RouteLink}
                                    to={buildUrl(categoryUrlBuilder(category))}
                                    sx={styles.link}
                                >
                                    {isPDP
                                        ? category?.displayName
                                            ? category?.displayName
                                            : category.name
                                        : category?.c_seoDisplayName || category?.name}
                                </ChakraBreadcrumbLink>
                            )}
                        </ChakraBreadcrumbItem>
                    )
                })}
                {product && (
                    <ChakraBreadcrumbItem
                        key={product.id}
                        data-testid="sf-crumb-item"
                        isCurrentPage
                    >
                        <ChakraBreadcrumbLink {...styles.lastitem}>
                            {product.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                )}

                {/* Use cases:
                    type = 'stores' - Stores page breadcrumbs,
                    type = 'content' - Content page breadcrumbs
                */}
                {children}
            </ChakraBreadcrumb>
        </>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array,
    product: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
    }),
    type: PropTypes.string,
    pageData: PropTypes.object,
    children: PropTypes.node
}

export default Breadcrumb
